import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    REQUEST_ESSENCE_MAX_LENGTH,
    AREA_SQUARE_MAX_LENGHT,
    BUDGET_MAX_LENGHT,
} from '@crm/entities/deal';
import PlusIcon from '@crm/shared/assets/icons/plus.svg?react';
import { DEFAULT_AREA_UNIT } from '@crm/shared/config';
import {
    cn,
    DateHelpers,
    formatPhoneInput,
    MAX_PHONE_LENGTH,
    sumFormFormatter,
    tw,
} from '@crm/shared/lib';
import {
    Button,
    type ButtonVariant,
    DatePicker,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    Field,
    FieldGroup,
    FieldLabel,
    InputError,
    InputGroup,
    InputGroupTextarea,
    SvgIcon,
} from '@crm/shared/ui';

import { defaultFormValues } from './model/consts';
import { createAddNewDealSchema } from './model/form.schema';
import { handleCreateDealError } from './model/handle-create-deal-error';
import { useCreateDealMutation } from './model/useCreateDealMutation';
import { DealTextField } from './ui';

import type { TFormInput, TFormState } from './model/form.schema';

interface IAddNewDealProps {
    isDisabled?: boolean;
    columnId?: number;
    areaUnit?: string;
    currencySymbol?: string;
    triggerBtnVariant?: ButtonVariant;
    triggerBtnStyles?: string;
    dialogTriggerStyles?: string;
}

export const AddNewDeal = ({
    isDisabled = false,
    columnId,
    areaUnit = DEFAULT_AREA_UNIT,
    currencySymbol = '₽',
    triggerBtnVariant,
    triggerBtnStyles,
    dialogTriggerStyles,
}: IAddNewDealProps) => {
    const { t } = useTranslation();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const createDealMutation = useCreateDealMutation();
    const isFormPending = createDealMutation.isPending;
    const addNewDealSchema = createAddNewDealSchema(t);

    const { clearErrors, control, handleSubmit, reset, setError, trigger } = useForm<
        TFormInput,
        unknown,
        TFormState
    >({
        resolver: zodResolver(addNewDealSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldFocusError: true,
        disabled: isFormPending,
        defaultValues: defaultFormValues,
    });

    const handleDialogOpenChange = (isOpen: boolean) => {
        setIsDialogOpen(isOpen);

        if (!isOpen) {
            clearErrors();
        }
    };

    const requiredFieldLabelStyles = tw(
        'gap-0 after:ml-px after:text-smtr-main-red-100 after:content-["*"]'
    );

    const onSubmit: SubmitHandler<TFormState> = (data) => {
        const payload = columnId === undefined ? data : { ...data, columnId };

        createDealMutation.mutate(payload, {
            onSuccess: () => {
                setIsDialogOpen(false);
                reset(defaultFormValues);
            },
            onError: (error) => {
                handleCreateDealError({
                    error,
                    fallback: t('crm.addNewDeal.error.create'),
                    setError,
                });
            },
        });
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger
                className={cn(dialogTriggerStyles)}
                render={
                    <Button
                        variant={triggerBtnVariant}
                        className={cn('px-4 py-2', triggerBtnStyles)}
                        disabled={isDisabled}
                    >
                        <SvgIcon icon={PlusIcon} className="size-2.5" />
                        <span className="text-xs leading-4 font-normal">
                            {t('crm.common.actions.addDeal')}
                        </span>
                    </Button>
                }
            />
            <DialogContent className="max-w-112.25 p-6" closeBtnStyles="top-4 right-4">
                <form className="contents" onSubmit={handleSubmit(onSubmit)}>
                    <DialogHeader>
                        <DialogTitle>{t('crm.addNewDeal.dialog.title')}</DialogTitle>
                    </DialogHeader>

                    <FieldGroup className="flex-row">
                        <Controller
                            name="dealName"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={field}
                                    fieldState={fieldState}
                                    label={t('crm.common.deal.title')}
                                    placeholder={t('crm.addNewDeal.fields.dealName.placeholder')}
                                />
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup className="flex-row">
                        <Controller
                            name="customerName"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={{
                                        ...field,
                                        onChange: (value) => {
                                            field.onChange(value);

                                            if (fieldState.invalid) {
                                                void trigger(field.name);
                                            }
                                        },
                                    }}
                                    fieldState={fieldState}
                                    label={t('crm.addNewDeal.fields.customerName.label')}
                                    labelClassName={requiredFieldLabelStyles}
                                    placeholder={t(
                                        'crm.addNewDeal.fields.customerName.placeholder'
                                    )}
                                />
                            )}
                        />
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={field}
                                    fieldState={fieldState}
                                    label={t('crm.addNewDeal.fields.phone.label')}
                                    placeholder="79991234567"
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={MAX_PHONE_LENGTH}
                                    formatter={formatPhoneInput}
                                />
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel htmlFor={field.name}>
                                        {t('crm.addNewDeal.fields.description.label')}
                                    </FieldLabel>
                                    <InputGroup>
                                        <InputGroupTextarea
                                            className={cn(
                                                'h-24.5 flex-none overflow-y-auto',
                                                fieldState.invalid && 'pr-7'
                                            )}
                                            id={field.name}
                                            aria-invalid={fieldState.invalid}
                                            aria-describedby={
                                                fieldState.error ? `${field.name}-error` : undefined
                                            }
                                            {...field}
                                            placeholder={t(
                                                'crm.addNewDeal.fields.description.placeholder'
                                            )}
                                            disabled={field.disabled}
                                            maxLength={REQUEST_ESSENCE_MAX_LENGTH}
                                            onChange={(event) => {
                                                field.onChange(event);

                                                if (fieldState.invalid) {
                                                    clearErrors(field.name);
                                                }
                                            }}
                                        />
                                        {fieldState.invalid && (
                                            <InputError
                                                tooltipId={`${field.name}-error`}
                                                errorMessage={fieldState.error?.message}
                                                className="absolute top-3 right-0 z-50 -translate-y-1/2 py-0 pr-2"
                                                iconClassName="size-3.5"
                                                tooltipClassName="mb-1 max-w-52"
                                                forceTooltipVisible
                                            />
                                        )}
                                    </InputGroup>
                                </Field>
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Controller
                            name="objectType"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Field>
                                    <DealTextField
                                        field={field}
                                        fieldState={fieldState}
                                        label={t('crm.addNewDeal.fields.objectType.label')}
                                        placeholder={t(
                                            'crm.addNewDeal.fields.objectType.placeholder'
                                        )}
                                    />
                                </Field>
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup className="flex-row">
                        <Controller
                            name="areaSquare"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={field}
                                    fieldState={fieldState}
                                    label={t('crm.addNewDeal.fields.area.label', {
                                        unit: areaUnit,
                                    })}
                                    placeholder="50"
                                    inputMode="decimal"
                                    formatter={sumFormFormatter}
                                    maxLength={AREA_SQUARE_MAX_LENGHT}
                                />
                            )}
                        />
                        <Controller
                            name="budget"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={field}
                                    fieldState={fieldState}
                                    label={t('crm.addNewDeal.fields.budget.label', {
                                        currency: currencySymbol,
                                    })}
                                    placeholder="200 000"
                                    inputMode="decimal"
                                    formatter={sumFormFormatter}
                                    maxLength={BUDGET_MAX_LENGHT}
                                />
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Controller
                            name="source"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DealTextField
                                    field={field}
                                    fieldState={fieldState}
                                    label={t('crm.addNewDeal.fields.source.label')}
                                    placeholder={t('crm.addNewDeal.fields.source.placeholder')}
                                />
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Controller
                            name="receivedAt"
                            control={control}
                            render={({ field, fieldState }) => (
                                <DatePicker
                                    id={field.name}
                                    name={field.name}
                                    label={t('crm.addNewDeal.fields.receivedAt.label')}
                                    placeholder={t('crm.addNewDeal.fields.receivedAt.placeholder')}
                                    value={field.value}
                                    onChange={(value) => {
                                        field.onChange(value);

                                        if (
                                            value.trim() === '' ||
                                            DateHelpers.parseDisplayDate(value)
                                        ) {
                                            clearErrors(field.name);
                                        }
                                    }}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                    disabled={field.disabled}
                                    invalid={fieldState.invalid}
                                    errorDisplay="tooltip"
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                    </FieldGroup>

                    <DialogFooter className="border-t-0 bg-white">
                        <DialogClose
                            render={
                                <Button type="button" variant="outline">
                                    {t('button.cancel.text')}
                                </Button>
                            }
                        />
                        <Button disabled={isFormPending} type="submit">
                            {t('crm.common.actions.createDeal')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
